import {
  getPayCyclePeriodsPerYear,
  getPayCycleLabel,
  hasNonMonthlyPayCycle,
} from "./payrollUtils";
import { computeFsaProration } from "@shared/utils/fsaProration";
import { isFsaPlanType } from "@shared/schemas/planTypes";
import type { PaySchedule } from "@shared/utils/payrollSchedule";

/** Minimal subset of `fsaPlanConfig` we read for the preview. Accepts numeric
 *  strings (the shape we get back from the API) or numbers. */
export interface FsaConfigForPreview {
  annualElectionMin?: string | number | null;
  annualElectionMax?: string | number | null;
  employerContribution?: string | number | null;
}

/** Everything the three FSA election surfaces need to render a live preview:
 *  per-paycheck cost, combined-cap status, and the employer's portion. */
export interface FsaPreview {
  /** Numeric coercion of the raw employee input. NaN/<=0 → 0. */
  employeeAnnual: number;
  /** True when the employee entered a positive finite number. */
  employeeAnnualIsValid: boolean;
  /** Annual / pay-periods. 0 if the input is invalid. Uses the EXACT remaining
   *  periods when provided (proration), else pay-periods-per-year. */
  perPaycheckEmployeeContribution: number;
  /** Display label matching the platform convention ("per month",
   *  "per bi-weekly", etc.). */
  perPaycheckLabel: string;
  /** Pay periods the election was divided by (remaining when prorated). */
  periodsUsed: number;
  /** True when divided by remaining pay periods, not the full year. */
  prorated: boolean;
  /** True when the remaining-period count is an estimate (no payday anchor). */
  estimated: boolean;
  /** Plan-level employer contribution from fsaPlanConfig. 0 if unset. */
  employerContribution: number;
  /** employerContribution + employeeAnnual (when valid). */
  combinedAnnual: number;
  /** Plan max from fsaPlanConfig.annualElectionMax. null if unset. */
  annualCap: number | null;
  /** True when combinedAnnual > annualCap. False if cap is null. */
  overCap: boolean;
}

/** Options for proration: divide by the EXACT remaining pay periods instead of
 *  the full year. `remainingPeriods` typically comes from computeFsaProration. */
export interface FsaPreviewOptions {
  /** Remaining pay periods (effective date → plan-year end). Falsy/≤0 → full-year. */
  remainingPeriods?: number | null;
  /** Mark the remaining-period count as estimated (weekly/bi-weekly, no anchor). */
  estimated?: boolean;
}

/** Pure computation — no React, safe in any render or submit handler. */
export function computeFsaPreview(
  rawEmployeeElection: string | number | null | undefined,
  fsaConfig: FsaConfigForPreview | null | undefined,
  payCycle: string | null | undefined,
  options?: FsaPreviewOptions,
): FsaPreview {
  const employeeAnnual = Number(rawEmployeeElection ?? 0);
  const employeeAnnualIsValid = Number.isFinite(employeeAnnual) && employeeAnnual > 0;

  const employerContribution =
    fsaConfig?.employerContribution != null ? Number(fsaConfig.employerContribution) : 0;
  const annualCap =
    fsaConfig?.annualElectionMax != null ? Number(fsaConfig.annualElectionMax) : null;

  const combinedAnnual = employerContribution + (employeeAnnualIsValid ? employeeAnnual : 0);
  const overCap = annualCap != null && Number.isFinite(annualCap) && combinedAnnual > annualCap;

  // Prorate over the exact remaining periods when given; else divide by the full year.
  const remaining = options?.remainingPeriods;
  const prorated = typeof remaining === "number" && Number.isFinite(remaining) && remaining > 0;
  const periodsUsed = prorated ? remaining! : getPayCyclePeriodsPerYear(payCycle);
  const perPaycheckEmployeeContribution = employeeAnnualIsValid
    ? employeeAnnual / periodsUsed
    : 0;

  const perPaycheckLabel = hasNonMonthlyPayCycle(payCycle)
    ? `per ${getPayCycleLabel(payCycle)}`
    : "per month";

  return {
    employeeAnnual,
    employeeAnnualIsValid,
    perPaycheckEmployeeContribution,
    perPaycheckLabel,
    periodsUsed,
    prorated,
    estimated: prorated && !!options?.estimated,
    employerContribution,
    combinedAnnual,
    annualCap,
    overCap,
  };
}

/** A plan is enrollment-ready once it has a positive annual election max. */
export function isFsaConfigured(config: FsaConfigForPreview | null | undefined): boolean {
  return config != null && Number(config.annualElectionMax) > 0;
}

/**
 * True when a plan is an FSA whose annual election limits aren't set yet — so
 * it's hidden from employees during enrollment until configured. Single source
 * for this "needs setup" check, shared by the broker plans list, the plan
 * detail warning, and the employee enrollment filters (keeps them in sync).
 */
export function fsaNeedsSetup(
  planType: string | null | undefined,
  config: FsaConfigForPreview | null | undefined,
): boolean {
  return isFsaPlanType(String(planType ?? "")) && !isFsaConfigured(config);
}

/** The employee's cost of an FSA election, for cost summaries/tallies. */
export interface FsaEmployeeCost {
  /** election ÷ 12 — a monthly-equivalent for monthly cost tallies. */
  monthlyEquivalent: number;
  /** Prorated per-paycheck deduction (same number the election preview shows). */
  perPaycheck: number;
  preview: FsaPreview;
}

/**
 * FSA plans carry no rate table, so the rate-based premium calc returns 0 — the
 * cost IS the election. This is the single source for that cost so the election
 * box, the per-step deduction, and the cost tally all agree. Pure: the caller
 * passes the resolved pay schedule, coverage start, and plan-year end.
 */
export function fsaEmployeeCost(args: {
  annualElection: string | number | null | undefined;
  fsaConfig: FsaConfigForPreview | null | undefined;
  payCycle?: string | null;
  /** Pay schedule for exact proration; omit to divide over the full year. */
  schedule?: PaySchedule | null;
  /** Coverage start (proration "from"); defaults to today when omitted. */
  effectiveDate?: string | Date | null;
  /** Plan-year end (proration "to"). Proration only runs when this + schedule exist. */
  planYearEnd?: string | Date | null;
}): FsaEmployeeCost {
  const { annualElection, fsaConfig, payCycle, schedule, effectiveDate, planYearEnd } = args;
  let options: FsaPreviewOptions | undefined;
  const to = planYearEnd ? new Date(planYearEnd) : null;
  if (schedule && to && !Number.isNaN(to.getTime())) {
    const from = effectiveDate ? new Date(effectiveDate) : new Date();
    const { remainingPeriods, isExact } = computeFsaProration({
      annualElection: Number(annualElection) || 0,
      schedule,
      from,
      to,
    });
    options = { remainingPeriods, estimated: !isExact };
  }
  const preview = computeFsaPreview(annualElection, fsaConfig, payCycle, options);
  return {
    monthlyEquivalent: (Number(annualElection) || 0) / 12,
    perPaycheck: preview.perPaycheckEmployeeContribution,
    preview,
  };
}

export type FsaElectionErrorCode =
  | "not_configured"
  | "no_pay_schedule"
  | "required"
  | "below_min"
  | "over_cap";

export interface FsaElectionValidation {
  ok: boolean;
  code?: FsaElectionErrorCode;
  message?: string;
}

/** Shown to employees when their pay schedule can't produce an exact deduction. */
export const FSA_NO_PAY_SCHEDULE_EMPLOYEE =
  "Your payroll schedule isn't set up yet — your employer needs to set it before you can enroll in this FSA plan.";
/** Shown to brokers/admins (they can fix it on the Payroll page). Audience-neutral —
 *  used in both the broker and employer portals (where the employer *is* the client). */
export const FSA_NO_PAY_SCHEDULE_ADMIN =
  "Set the payroll schedule (a pay group with a pay date) on the Payroll page before enrolling in this FSA plan.";

/** Single source of truth for whether an FSA election may be saved. Mirrors the
 *  server guard: plan must be configured, election present, ≥ min, and the
 *  combined employer + employee amount must not exceed the cap. */
export function validateFsaElection(
  rawEmployeeElection: string | number | null | undefined,
  config: FsaConfigForPreview | null | undefined,
  payCycle?: string | null,
  planName?: string,
  options?: { payScheduleReady?: boolean },
): FsaElectionValidation {
  const label = planName || "this FSA plan";
  const preview = computeFsaPreview(rawEmployeeElection, config, payCycle);

  if (!isFsaConfigured(config) || preview.annualCap == null) {
    return { ok: false, code: "not_configured", message: `${label} isn't available for enrollment yet.` };
  }
  // Exact deduction requires a ready payroll schedule (no estimate).
  if (options?.payScheduleReady === false) {
    return { ok: false, code: "no_pay_schedule", message: FSA_NO_PAY_SCHEDULE_EMPLOYEE };
  }
  if (!preview.employeeAnnualIsValid) {
    return { ok: false, code: "required", message: `Enter the annual election for ${label}.` };
  }
  const min = config?.annualElectionMin != null ? Number(config.annualElectionMin) : null;
  if (min != null && preview.employeeAnnual < min) {
    return { ok: false, code: "below_min", message: `${label} requires at least $${min.toLocaleString()}.` };
  }
  if (preview.overCap) {
    const employerNote = preview.employerContribution > 0
      ? ` (employer adds $${preview.employerContribution.toLocaleString()})`
      : "";
    return { ok: false, code: "over_cap", message: `${label} cannot exceed $${preview.annualCap.toLocaleString()}${employerNote}. Lower the election to continue.` };
  }
  return { ok: true };
}
